/**
 * ReportTranscript — Thin wrapper: DB transcript rows → TranscriptViewer.
 * Maps database column names to TranscriptViewer's expected props.
 */
import React, { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Section, SectionTitle, Paragraph } from './reportStyles';
import { SCENARIO_COLORS_BY_NAME } from '../../config/brandColors';
import TranscriptViewer from './TranscriptViewer';
import EditableText from './EditableText';
import { useTranslation } from '../../hooks/useTranslation.jsx';

// Chosen perspective card (Näkökulman rajaus) — Sitra: black left bar + flat surface
const PerspectiveCard = styled.div`
  background: #f5f5f5;
  border-left: 6px solid #000;
  padding: 20px 24px;
  margin: 16px 0 24px;
`;

const PerspectiveLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #000;
  margin-bottom: 8px;
`;

const PerspectiveTitle = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: #000;
  line-height: 1.5;
`;

const ExpandToggle = styled.button`
  background: none;
  border: none;
  color: #367ABA;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-top: 12px;
  &:hover { text-decoration: underline; }
`;

const PerspectiveList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
`;

const PerspectiveRow = styled.div`
  font-size: 14px;
  line-height: 1.5;
  padding: 8px 12px;
  border-radius: 2px;
  background: ${p => p.$selected ? '#367ABA' : 'rgba(255,255,255,0.7)'};
  color: ${p => p.$selected ? '#fff' : '#6b7280'};
  font-weight: ${p => p.$selected ? 600 : 400};
`;

export default function ReportTranscript({ sectionId, title, description, segments = [], highlightCount, scenarioChoices, chosenPerspective, allPerspectives }) {
  const { t } = useTranslation();
  const [showAllPerspectives, setShowAllPerspectives] = useState(false);
  // Anonymize speakers: map real names to "Puhuja 1", "Puhuja 2", etc.
  // Keep non-personal labels (Fasilitaattori, Ryhmä X) as-is
  const comments = useMemo(() => {
    const speakerMap = {};
    let speakerIdx = 0;

    function anonymize(speakerId, label) {
      if (!label && !speakerId) return t('report.transcript.speaker');
      // Keep group/role labels (no personal names)
      if (label && /^(Fasilitaattori|Ryhmä|Teema|Ilmasto|Kemian|Kalliol)/i.test(label)) {
        return label.split(',')[0]; // "Ryhmä 4, ehdotus 1" → "Ryhmä 4"
      }
      const key = speakerId || label;
      if (!speakerMap[key]) {
        speakerIdx++;
        speakerMap[key] = `${t('report.transcript.speaker')} ${speakerIdx}`;
      }
      return speakerMap[key];
    }

    return segments.map((s, i) => ({
      id: s.id || i,
      speaker: anonymize(s.speaker_id ?? s.speaker, s.speaker_label ?? s.label),
      text: s.text,
      summary: s.summary || null,
      startTime: s.start_time ?? s.startTime,
    }));
  }, [segments]);

  if (!comments.length) return null;

  return (
    <Section id={sectionId}>
      <SectionTitle>{title}</SectionTitle>
      {description && <Paragraph>{description}</Paragraph>}

      {chosenPerspective && (
        <PerspectiveCard>
          <PerspectiveLabel>{t('report.transcript.perspectiveLabel')}</PerspectiveLabel>
          <PerspectiveTitle>
            {chosenPerspective.id
              ? <EditableText fieldKey={`perspective.${chosenPerspective.id}.title`} fallback={chosenPerspective.title} multiline={false} label="Valitun näkökulman otsikko" />
              : chosenPerspective.title}
          </PerspectiveTitle>
          {allPerspectives?.length > 1 && (
            <>
              <ExpandToggle onClick={() => setShowAllPerspectives(v => !v)}>
                {showAllPerspectives ? <>{t('report.transcript.hideAllPerspectives')} <ChevronUp size={14} /></> : <>{t('report.transcript.showAllPerspectives', { count: allPerspectives.length })} <ChevronDown size={14} /></>}
              </ExpandToggle>
              {showAllPerspectives && (
                <PerspectiveList>
                  {allPerspectives.map(p => (
                    <PerspectiveRow key={p.id} $selected={p.id === chosenPerspective.id}>
                      {p.order_index}. {p.title}
                    </PerspectiveRow>
                  ))}
                </PerspectiveList>
              )}
            </>
          )}
        </PerspectiveCard>
      )}

      <TranscriptViewer
        comments={comments}
        title={title}
        highlightCount={highlightCount}
        highlightLabels={scenarioChoices?.map(s => ({
          text: s.selected_tulevaisuuskuva
            ? `${s.title}: "${s.selected_tulevaisuuskuva}"`
            : s.title,
          color: SCENARIO_COLORS_BY_NAME[s.title] || '#6b7280',
          matchKeys: [s.title, s.selected_tulevaisuuskuva]
            .filter(Boolean)
            .map(t => t.toLowerCase()),
        }))}
      />
    </Section>
  );
}
